/**
 * Selo de correlação de um recurso compartilhado: quantas entidades estão ligadas a ele (a
 * "quantidade de vínculos") e, ao passar o mouse, quem são — vindo direto do grafo.
 *
 * É a correlação tornada visível no lugar onde o dado vive: um telefone/endereço/rede que
 * aparece em mais de um cadastro acende (`aceso`), sinalizando conexão. O tooltip é CSS puro
 * (group-hover/focus), sem dependência de tooltip.
 */
import { Link2 } from 'lucide-react';
import type { CorrelacaoCad } from '@/lib/api';
import { rotuloTipo } from '@/lib/recursos/rotulos';
import { cn } from '@/lib/utils';

export function BadgeCorrelacao({ correlacao }: { correlacao?: CorrelacaoCad }) {
  if (!correlacao) return null;
  const total = correlacao.total;
  if (total <= 0) return null;
  const compartilhado = total >= 2; // ligado a 2+ entidades = correlação de verdade

  return (
    <span className="group/corr relative inline-flex">
      <span
        tabIndex={0}
        aria-label={`${total} ${total === 1 ? 'vínculo' : 'vínculos'}`}
        className={cn(
          'inline-flex cursor-default items-center gap-1 rounded-sm border px-1.5 py-0.5',
          'font-mono text-xs tabular-nums outline-none focus-visible:shadow-[var(--focus-ring)]',
          compartilhado
            ? 'border-primary/40 text-primary'
            : 'border-border bg-surface-2 text-text-tertiary',
        )}
      >
        <Link2 className="size-3" />
        {total}
      </span>

      {/* Tooltip: aparece no hover/focus do selo. Lista quem compartilha o recurso. */}
      <span
        role="tooltip"
        className={cn(
          'pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-max max-w-64 -translate-x-1/2',
          'rounded-md border border-border-strong bg-surface p-3 text-left shadow-lg',
          'opacity-0 transition-opacity group-hover/corr:opacity-100 group-focus-within/corr:opacity-100',
        )}
      >
        <span className="tipo-rotulo mb-1 block text-text-tertiary">
          {compartilhado ? 'Compartilhado com' : 'Vinculado a'}
        </span>
        <span className="flex flex-col gap-1">
          {correlacao.donos.map((d) => (
            <span key={`${d.tipo}-${d.id}`} className="flex items-baseline gap-1.5 text-[13px]">
              <span className="text-text-tertiary">{rotuloTipo(d.tipo)}</span>
              <span className="text-text">{d.nome}</span>
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}
